import { useContext, useEffect, useRef, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  Paper,
  Divider,
  InputAdornment,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { CRUDContext } from "./ServicesTable";
import { endpoints } from "../../Utils/UrlEnum";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { ProductFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import {
  ProductFormValidationSchema,
  ProductPriceModel,
} from "../../Utils/Models";
import { onFormError } from "../../Utils/Utils";
import useCustomMemo from "../../Hooks/useCustomMemo";
import DocumentTypeAutocomplete from "../Documents/DocumentTypeAutocomplete";

export default function ServicesForm(props: ProductFormProps) {
  // Use the provided vehicle or fallback to the CRUDContext
  const serviceContext: any = useContext(
    props.selectedProduct ? props.selectedProduct : CRUDContext
  );
  const formRef = useRef<HTMLFormElement>(null);
  const firstUpdate = useRef(true);
  const memo = useCustomMemo();
  const workPoints = memo?.storage?.workPoints || [];
  const [workPointPrices, setWorkPointPrices] = useState<ProductPriceModel[]>(
    []
  );
  const [supplementaryHoursPrices, setSupplementaryHoursPrices] = useState<
    ProductPriceModel[]
  >([]);

  // Initialize the form with Yup validation and default values
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    formState,
    setValue,
  } = useForm({
    resolver: yupResolver(ProductFormValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      monitorThresholdMonths: Config.defaulotMonitorThresholdMonths,
    },
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  const formData = useWatch({ control });

  // Update context when form data changes
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({ ...formData });
    }
  }, [formData]);

  // Automatically submit the form if needed (based on your original logic)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    formRef.current?.submit();
  }, []);

  // Consolidated effect to handle both form reset and workpoint prices
  useEffect(() => {
    // First, reset the form with serviceContext data if available
    if (serviceContext.name && serviceContext.name !== "") {
      reset({
        ...serviceContext,
        documentTypes:
          serviceContext.documentTypes?.map((d: any) =>
            typeof d === "object" ? d._id : d
          ) || [],
      });
    }

    // Then, initialize workpoint prices when workpoints are available
    if (workPoints && workPoints.length > 0) {
      if (serviceContext && serviceContext._id) {
        // Editing existing product - merge existing prices with all workpoints
        const existingPrices = serviceContext.prices || [];
        const existingSupplementaryPrices =
          serviceContext.supplementaryHoursPrices || [];
        const allWorkPointPrices = workPoints.map((wp: any) => {
          const existingPrice = existingPrices.find(
            (p: any) => p.workPointId?._id === wp._id
          );
          return {
            workPointId: wp._id,
            price: existingPrice?.price || "",
          };
        });
        const allSupplementaryPrices = workPoints.map((wp: any) => {
          const existingSupplementaryPrice = existingSupplementaryPrices.find(
            (p: any) => p.workPointId?._id === wp._id
          );
          return {
            workPointId: wp._id,
            price: existingSupplementaryPrice?.price || "",
          };
        });
        setWorkPointPrices(allWorkPointPrices);
        setSupplementaryHoursPrices(allSupplementaryPrices);
      } else {
        // Creating new product - populate all workpoints with empty prices
        const allWorkPointPrices = workPoints.map((wp: any) => ({
          workPointId: wp._id,
          price: "",
        }));
        const allSupplementaryPrices = workPoints.map((wp: any) => ({
          workPointId: wp._id,
          price: "",
        }));
        setWorkPointPrices(allWorkPointPrices);
        setSupplementaryHoursPrices(allSupplementaryPrices);
      }
    }
  }, [workPoints, serviceContext, reset]);

  // Update an existing element
  const updateElement = async (model: any) => {
    const url = `${endpoints.product.main}/${serviceContext?._id}`;

    try {
      const res = (await updateData(url, model)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.setContextValue([]);
      props.onClose();
    } catch (error) {
      console.error("Error updating vehicle:", error);
    }
  };

  // Create a new element

  const createElement = async (model: any) => {
    const url = `${endpoints.product.main}`;

    try {
      const res = (await postData(url, model)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.onClose();
      props.setContextValue([]);
    } catch (error) {
      console.error("Error creating instructor:", error);
    }
  };

  // Function to update workpoint price
  const updateWorkPointPrice = (index: number, price: string) => {
    const updatedPrices = [...workPointPrices];
    updatedPrices[index] = { ...updatedPrices[index], price };
    setWorkPointPrices(updatedPrices);
  };

  // Function to update supplementary hour price
  const updateSupplementaryHourPrice = (index: number, price: string) => {
    const updatedPrices = [...supplementaryHoursPrices];
    updatedPrices[index] = { ...updatedPrices[index], price };
    setSupplementaryHoursPrices(updatedPrices);
  };

  // Handle form submission
  const onSubmit = (model: any) => {
    // Add workpoint prices to the model (include all entries, even with empty prices)
    const modelWithPrices = {
      ...model,
      prices: workPointPrices, // Include all workpoint entries
      ...(model.isCertificate && { supplementaryHoursPrices }), // Include supplementary prices only if certificate
      supplementaryHoursPrices,
    };
    serviceContext?._id
      ? updateElement(modelWithPrices)
      : createElement(modelWithPrices);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={formRef}
      id={FormsIds.productForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        {/* Basic vehicle fields */}
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("name")}
            id="name"
            name="name"
            label={Vocabulary.name}
            value={getValues("name") || ""}
            fullWidth
            variant="outlined"
            error={!!formState.errors.name}
            helperText={formState.errors.name?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("defaultDrivingHours")}
            value={getValues("defaultDrivingHours") || ""}
            name="defaultDrivingHours"
            id="defaultDrivingHours"
            type="number"
            label={Vocabulary.defaultDrivingHours}
            fullWidth
            variant="outlined"
            error={!!formState.errors.defaultDrivingHours}
            helperText={formState.errors.defaultDrivingHours?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("monitorThresholdMonths")}
            value={getValues("monitorThresholdMonths") || ""}
            name="monitorThresholdMonths"
            id="monitorThresholdMonths"
            type="number"
            label={Vocabulary.monitorProduct}
            fullWidth
            variant="outlined"
            error={!!formState.errors.monitorThresholdMonths}
            helperText={formState.errors.monitorThresholdMonths?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Autocomplete
            options={Object.keys(props.examCategories ?? []) ?? []}
            getOptionLabel={(option: any) =>
              props.examCategories?.[option] || ""
            }
            value={getValues("examCategory") ?? null}
            onChange={(_, newValue) => {
              setValue("examCategory", newValue || "");
            }}
            renderInput={(params) => (
              <TextField
                inputRef={register("examCategory").ref}
                {...params}
                label={Vocabulary.examCategory}
                name="examCategory"
                id="examCategory"
                error={!!formState.errors.examCategory}
                helperText={formState.errors.examCategory?.message?.toString()}
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{ display: "flex", alignItems: "center" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                {...register("isCertificate")}
                checked={watch(`isCertificate`) ?? false}
                name="isCertificate"
                id="isCertificate"
              />
            }
            label={Vocabulary.isCertificate}
          />
          <FormControlLabel
            control={
              <Checkbox
                {...register("automata")}
                checked={watch(`automata`) ?? false}
                name="automata"
                id="automata"
              />
            }
            label={Vocabulary.automata}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{ display: "flex", alignItems: "center" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                {...register("visibleForOnlineEnrollment")}
                checked={watch(`visibleForOnlineEnrollment`) ?? false}
                name="visibleForOnlineEnrollment"
                id="visibleForOnlineEnrollment"
              />
            }
            label={Vocabulary.visibleForOnlineEnrollment}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="h6" component="h3" mb={2}>
            Documente Obligatorii
          </Typography>
          <DocumentTypeAutocomplete
            control={control}
            name="documentTypes"
            showManageButton={true}
            multiple={true}
            error={formState.errors.documentTypes as any}
          />
        </Grid>

        {/* Workpoint-based Pricing Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" component="h3" mb={2}>
            Prețuri pe Locații
          </Typography>

          {workPointPrices.map((priceEntry, index) => {
            const workPoint = workPoints.find(
              (wp: any) => wp._id === priceEntry.workPointId
            );
            const supplementaryEntry = supplementaryHoursPrices[index];
            return (
              <Paper key={priceEntry.workPointId} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems={"flex-start"}>
                  <Grid item xs={12} sm={12} md={4}>
                    <Typography variant="body1" sx={{ fontWeight: 500, pt: 1 }}>
                      {workPoint?.name || "Locație necunoscută"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      value={priceEntry.price || ""}
                      onChange={(e) =>
                        updateWorkPointPrice(index, e.target.value)
                      }
                      type="number"
                      label={Vocabulary.price}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">RON</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  {!watch("isCertificate") && (
                    <Grid item xs={12} sm={12} md={4}>
                      <TextField
                        value={supplementaryEntry?.price || ""}
                        onChange={(e) =>
                          updateSupplementaryHourPrice(index, e.target.value)
                        }
                        type="number"
                        label={Vocabulary.supplementaryHourPrice}
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">RON</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            );
          })}

          {workPointPrices.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic", mt: 1 }}
            >
              Se încarcă locațiile...
            </Typography>
          )}
        </Grid>
      </Grid>
    </form>
  );
}
